import React, { useEffect, useRef, useState } from 'react';
import config from "config";
import {
  changeCreateDeviceViewState,
  updateCustomersList
} from "../../ducks/customersDuck";
import { useDispatch } from "react-redux";
import { useEditViewSiteName, useCustomersList } from "../../hooks/useCloudSelectors";
import DeleteModal from "../DeleteModal/DeleteModal";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { CustomerConfig } from "../../../src/Dashboard/actions/apiActions";

const DeviceEditView = ({ device, highlighted }) => {
  const isNew = !!device.createDeviceView;
  const cardRef = useRef(null);

  const [deviceId, setDeviceId] = useState(device.deviceId);
  const [deviceName, setDeviceName] = useState(device.deviceName);
  const [edgeUnitId, setEdgeUnitId] = useState(device.edgeUnitId || '');
  const [edgeUnitName, setEdgeUnitName] = useState(device.edgeUnitName || '');
  const [deviceConfig, setDeviceConfig] = useState(device.config);
  const [solutionType, setSolutionType] = useState(device.solutionType || '');
  const [errors, setErrors] = useState({});

  const [dataChanged, setDataChanged] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const siteName = useEditViewSiteName();
  const allCustomers = useCustomersList();
  const dispatch = useDispatch();
  const initialRender = useRef(true);

  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [highlighted]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setDataChanged(true);
    }
  }, [deviceId, deviceName, deviceConfig, solutionType]);

  useEffect(() => {
    if (isNew) setDataChanged(true);
  }, []);

  const validate = () => {
    const e = {};
    if (!deviceId) e.deviceId = "Required";
    if (!deviceName) e.deviceName = "Required";
    if (!deviceConfig) e.deviceConfig = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    if (isNew) {
      const newDevice = { deviceId, deviceName, config: deviceConfig, solutionType: solutionType || undefined };
      const updatedAllCustomers = allCustomers.map(c => c.customerName === siteName
        ? { ...c, devices: [...c.devices, newDevice] }
        : c
      );
      await CustomerConfig.post(config.CONFIGURATION_URL, { customers: updatedAllCustomers });
      setDataChanged(false);
      dispatch(updateCustomersList(updatedAllCustomers));
      dispatch(changeCreateDeviceViewState());
    } else {
      const updatedCustomersObject = {
        customers: allCustomers.map(c => c.customerName === siteName
          ? {
            ...c,
            devices: c.devices.map(d => d.deviceId === device.deviceId
              ? { ...d, deviceId, deviceName, config: deviceConfig, solutionType: solutionType || d.solutionType }
              : d
            )
          }
          : c
        )
      };
      await CustomerConfig.post(config.CONFIGURATION_URL, updatedCustomersObject);
      dispatch(updateCustomersList(updatedCustomersObject.customers));
      setDataChanged(false);
    }
  };

  const handleReset = () => {
    if (isNew) {
      dispatch(changeCreateDeviceViewState());
      return;
    }
    setEdgeUnitId(device.edgeUnitId || '');
    setEdgeUnitName(device.edgeUnitName || '');
    setDeviceConfig(device.config);
    setSolutionType(device.solutionType || '');
    setErrors({});
    setTimeout(() => setDataChanged(false), 0);
  };

  return (
    <>
      {deleteModal && (
        <DeleteModal
          siteName={siteName}
          deviceId={device.deviceId}
          toggleDeleteModal={() => setDeleteModal(false)}
          type="device"
        />
      )}
      <Card
        ref={cardRef}
        variant="outlined"
        sx={highlighted ? {
          borderColor: "primary.main",
          borderWidth: 2,
          boxShadow: (theme) => `0 0 8px ${theme.palette.primary.main}40`,
        } : undefined}
      >
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {isNew ? "New System" : device.deviceName}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="System ID"
                value={edgeUnitId}
                onChange={(e) => setEdgeUnitId(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.edgeUnitId}
                helperText={errors.edgeUnitId}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="System Name"
                value={edgeUnitName}
                onChange={(e) => setEdgeUnitName(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.edgeUnitName}
                helperText={errors.edgeUnitName}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Config File"
                value={deviceConfig}
                onChange={(e) => setDeviceConfig(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.deviceConfig}
                helperText={errors.deviceConfig}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={["cems", "lora", "steamtrap"]}
                value={solutionType}
                onInputChange={(_, v) => setSolutionType(v)}
                size="small"
                renderInput={(params) => <TextField {...params} label="Solution Type" />}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2, gap: 1 }}>
          {!isNew && !dataChanged && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteModal(true)}
              size="small"
            >
              Delete
            </Button>
          )}
          {dataChanged && (
            <>
              <Button
                variant="outlined"
                startIcon={isNew ? <CloseIcon /> : <UndoIcon />}
                onClick={handleReset}
                size="small"
              >
                {isNew ? "Cancel" : "Undo"}
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                size="small"
              >
                {isNew ? "Add System" : "Save"}
              </Button>
            </>
          )}
        </CardActions>
      </Card>
    </>
  );
};

export default DeviceEditView;
